import { ReactNode } from 'react';

import { DisplayModes } from 'services/webedi/types';
import { WebEDIErrorsServiceSingleton } from 'services/webediErrors';
import { DATA_CHECK_ERROR_CODE } from 'support/helpers/const/const';
import { IssueResolverType, MessageV2DTO, PartnerDTO, TransactionState, ValueOf } from 'support/types';

import { CustomContentPreset } from './bannerPresets/CustomContentPreset';
import { DeliveredWarningPreset } from './bannerPresets/DeliveredWarningPreset';
import { GenericBlockingPreset } from './bannerPresets/GenericBlockingPreset';
import { OtherPartnerResolverAnnotationsPreset } from './bannerPresets/OtherPartnerResolverAnnotationsPreset';
import { OtherPartnerResolverNoActionPreset } from './bannerPresets/OtherPartnerResolverNoActionPreset';
import { ProcurosResolverGenericBlockingPreset } from './bannerPresets/ProcurosResolverGenericBlockingPreset';
import { WarningPreset } from './bannerPresets/WarningPreset';
import { WithErrorPreset } from './bannerPresets/WithErrorPreset';

type WebEDIMessageErrorBannerProps = {
  mode: DisplayModes;
  issueResolver?: IssueResolverType;
  isCurrentPartnerReceiver?: boolean;
  senderPartner?: PartnerDTO | null;
  receiverPartner?: PartnerDTO | null;
  isLoadingDatachecksErrors?: boolean;
  messageState?: ValueOf<TransactionState> | null;
  content?: ReactNode;
  blockingError?: MessageV2DTO['blocking_error'];
};

export const WebEDIMessageErrorBanner = ({
  mode,
  issueResolver,
  isCurrentPartnerReceiver,
  senderPartner,
  receiverPartner,
  isLoadingDatachecksErrors,
  messageState,
  content,
  blockingError,
}: WebEDIMessageErrorBannerProps) => {
  const errors = WebEDIErrorsServiceSingleton.useNumberOfErrors();
  const warnings = WebEDIErrorsServiceSingleton.useNumberOfWarnings();

  const isDatachecksBlocking = blockingError?.code === DATA_CHECK_ERROR_CODE;
  const isLoading = !!isLoadingDatachecksErrors;
  const isDeliveredMessage = messageState === TransactionState.success;

  // PROCUROS resolver: collapses datachecks + non-datachecks blocking. When
  // there is no blocking error, we render nothing — Procuros is handling the
  // issue and there is no actionable copy to show the user.
  if (issueResolver === IssueResolverType.PROCUROS) {
    if (blockingError) {
      return <ProcurosResolverGenericBlockingPreset blockingError={blockingError} />;
    }
    return null;
  }

  // OTHER_PARTNER resolver
  if (issueResolver === IssueResolverType.OTHER_PARTNER) {
    if (blockingError && !isDatachecksBlocking) {
      return <OtherPartnerResolverNoActionPreset isCurrentPartnerReceiver={isCurrentPartnerReceiver} />;
    }
    if (isDatachecksBlocking || errors > 0 || isLoading) {
      return (
        <OtherPartnerResolverAnnotationsPreset
          isCurrentPartnerReceiver={isCurrentPartnerReceiver}
          senderPartner={senderPartner}
          receiverPartner={receiverPartner}
          numberOfErrors={errors}
          isLoading={isLoading}
        />
      );
    }
    return null;
  }

  // CURRENT_PARTNER resolver (or undefined). Legacy quirk: the GenericBlocking
  // path uses neutral severity unless issueResolver === CURRENT_PARTNER
  // explicitly; undefined fell back to neutral.
  if (blockingError && !isDatachecksBlocking) {
    const severity = issueResolver === IssueResolverType.CURRENT_PARTNER ? 'error' : 'neutral';
    return <GenericBlockingPreset blockingError={blockingError} severity={severity} />;
  }

  if (errors > 0 || isLoading) {
    return (
      <WithErrorPreset
        mode={mode}
        errors={errors}
        warnings={warnings}
        isLoading={isLoading}
        blockingError={blockingError}
        overrideContent={content}
      />
    );
  }

  if (warnings > 0) {
    return isDeliveredMessage ? <DeliveredWarningPreset warnings={warnings} /> : <WarningPreset warnings={warnings} />;
  }

  if (content) {
    return <CustomContentPreset>{content}</CustomContentPreset>;
  }

  return null;
};
